// Weather service using Open-Meteo (free, no API key required)

import * as Sentry from '@sentry/react'
import { db } from './firebase'
import { doc, setDoc, getDocs, collection, query, where } from 'firebase/firestore'

const DEFAULT_LAT = -23.0
const DEFAULT_LON = 30.4

// Department-specific coordinates
const DEPARTMENT_COORDS = {
  kitchen: { lat: -23.0, lon: 30.4, label: 'Limpopo, SA' },
  bakery: { lat: -23.0, lon: 30.4, label: 'Limpopo, SA' },
  yawehs: { lat: -25.7, lon: 28.2, label: 'Pretoria, SA' },
}

export function getDepartmentCoords(department) {
  return DEPARTMENT_COORDS[department] || DEPARTMENT_COORDS.kitchen
}

const WEATHER_CACHE_KEY = 'desres_weather_cache'
const WEATHER_HISTORY_KEY = 'desres_weather_history'

const WMO_CODES = {
  0: { label: 'Clear sky', icon: 'sun' },
  1: { label: 'Mainly clear', icon: 'sun' },
  2: { label: 'Partly cloudy', icon: 'cloud-sun' },
  3: { label: 'Overcast', icon: 'cloud' },
  45: { label: 'Fog', icon: 'cloud-fog' },
  48: { label: 'Depositing rime fog', icon: 'cloud-fog' },
  51: { label: 'Light drizzle', icon: 'cloud-drizzle' },
  53: { label: 'Moderate drizzle', icon: 'cloud-drizzle' },
  55: { label: 'Dense drizzle', icon: 'cloud-drizzle' },
  61: { label: 'Slight rain', icon: 'cloud-rain' },
  63: { label: 'Moderate rain', icon: 'cloud-rain' },
  65: { label: 'Heavy rain', icon: 'cloud-rain' },
  80: { label: 'Slight showers', icon: 'cloud-rain' },
  81: { label: 'Moderate showers', icon: 'cloud-rain' },
  82: { label: 'Violent showers', icon: 'cloud-rain' },
  95: { label: 'Thunderstorm', icon: 'cloud-lightning' },
  96: { label: 'Thunderstorm + hail', icon: 'cloud-lightning' },
  99: { label: 'Thunderstorm + heavy hail', icon: 'cloud-lightning' },
}

function getWeatherInfo(code) {
  return WMO_CODES[code] || { label: 'Unknown', icon: 'cloud' }
}

// Fetch 7-day forecast + current weather
export async function fetchWeatherForecast(lat = DEFAULT_LAT, lon = DEFAULT_LON) {
  const cacheKey = `${lat},${lon}`
  const cached = loadCache()
  if (cached && cached.coordKey === cacheKey && Date.now() - cached.fetchedAt < 30 * 60 * 1000) {
    return cached.data
  }

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
      + `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,uv_index_max`
      + `&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m`
      + `&timezone=Africa/Johannesburg&forecast_days=7`

    const res = await fetch(url)
    if (!res.ok) throw new Error(`Weather API error: ${res.status}`)
    const json = await res.json()

    const forecast = json.daily.time.map((date, i) => ({
      date,
      weatherCode: json.daily.weather_code[i],
      ...getWeatherInfo(json.daily.weather_code[i]),
      tempMax: json.daily.temperature_2m_max[i],
      tempMin: json.daily.temperature_2m_min[i],
      precipitation: json.daily.precipitation_sum[i],
      windMax: json.daily.wind_speed_10m_max[i],
      uvIndex: json.daily.uv_index_max[i],
    }))

    const current = {
      temp: json.current.temperature_2m,
      humidity: json.current.relative_humidity_2m,
      weatherCode: json.current.weather_code,
      ...getWeatherInfo(json.current.weather_code),
      windSpeed: json.current.wind_speed_10m,
    }

    const data = { current, forecast, fetchedAt: new Date().toISOString() }
    saveCache(data, cacheKey)
    return data
  } catch (err) {
    console.warn('Weather fetch failed:', err.message)
    if (cached) return cached.data
    return null
  }
}

// Fetch historical weather for a specific date range
export async function fetchHistoricalWeather(startDate, endDate, lat = DEFAULT_LAT, lon = DEFAULT_LON) {
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
      + `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max`
      + `&timezone=Africa/Johannesburg&start_date=${startDate}&end_date=${endDate}`

    const res = await fetch(url)
    if (!res.ok) throw new Error(`Historical weather API error: ${res.status}`)
    const json = await res.json()

    return json.daily.time.map((date, i) => ({
      date,
      weatherCode: json.daily.weather_code[i],
      ...getWeatherInfo(json.daily.weather_code[i]),
      tempMax: json.daily.temperature_2m_max[i],
      tempMin: json.daily.temperature_2m_min[i],
      precipitation: json.daily.precipitation_sum[i],
      windMax: json.daily.wind_speed_10m_max[i],
    }))
  } catch (err) {
    console.warn('Historical weather fetch failed:', err.message)
    return []
  }
}

// --- Local cache ---

function loadCache() {
  try {
    const raw = localStorage.getItem(WEATHER_CACHE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveCache(data, coordKey) {
  localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ data, fetchedAt: Date.now(), coordKey }))
}

// --- Weather history (logged alongside sales) ---

export function loadWeatherHistory() {
  try {
    const raw = localStorage.getItem(WEATHER_HISTORY_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function saveWeatherHistory(history) {
  localStorage.setItem(WEATHER_HISTORY_KEY, JSON.stringify(history))
}

export function logWeatherForDate(date, weatherData) {
  const history = loadWeatherHistory()
  const existing = history.findIndex(h => h.date === date)
  const record = {
    id: date,
    date,
    ...weatherData,
    loggedAt: new Date().toISOString(),
  }

  if (existing >= 0) {
    history[existing] = record
  } else {
    history.push(record)
  }
  saveWeatherHistory(history)

  // Sync to Firestore — non-critical feature, graceful degradation
  // is acceptable but still worth capturing.
  const docRef = doc(db, 'weatherHistory', date)
  setDoc(docRef, record).catch(err => {
    console.warn('[weather] weatherHistory setDoc failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'weather', fn: 'saveWeatherRecord.setDoc', date } })
  })

  return record
}

export function getWeatherForDate(date) {
  const history = loadWeatherHistory()
  return history.find(h => h.date === date) || null
}

export function getWeatherInRange(startDate, endDate) {
  const history = loadWeatherHistory()
  return history.filter(h => h.date >= startDate && h.date <= endDate)
}

// --- Weather-based production suggestions ---

export function getProductionSuggestions(tomorrowWeather, historicalCorrelations, departmentCategories) {
  const suggestions = []

  if (!tomorrowWeather) return suggestions

  // Build a set of valid category names for the active department (lowercase for matching)
  const validCats = departmentCategories
    ? new Set(departmentCategories.map(c => (c.name || c).toLowerCase()))
    : null

  // Helper: filter a list of category names to only those that exist in this department
  const filterCats = (cats) => {
    if (!validCats || !cats.length) return cats
    return cats.filter(c => validCats.has(c.toLowerCase()))
  }

  const isRainy = tomorrowWeather.precipitation > 5
  const isHot = tomorrowWeather.tempMax > 32
  const isCold = tomorrowWeather.tempMax < 18
  const isStormy = [95, 96, 99].includes(tomorrowWeather.weatherCode)
  const isClear = [0, 1].includes(tomorrowWeather.weatherCode)

  // Base suggestions from weather conditions — only include if at least one category matches the department
  if (isRainy || isStormy) {
    const reduceCats = filterCats(['steak', 'vors', 'stew vors'])
    if (reduceCats.length > 0) {
      suggestions.push({
        type: 'reduce',
        severity: 'HIGH',
        message: `Rain/storms expected (${tomorrowWeather.precipitation}mm). Reduce production by 20-30% — fewer walk-ins expected.`,
        categories: reduceCats,
      })
    }
    const warmCats = filterCats(['mala', 'chicken liver', 'mashondzha'])
    if (warmCats.length > 0) {
      suggestions.push({
        type: 'maintain',
        severity: 'MEDIUM',
        message: 'Stews and warm meals tend to sell well in rainy weather. Maintain or slightly increase.',
        categories: warmCats,
      })
    }
    // If no specific categories matched, still show a general rain warning
    if (reduceCats.length === 0 && warmCats.length === 0) {
      suggestions.push({
        type: 'reduce',
        severity: 'HIGH',
        message: `Rain/storms expected (${tomorrowWeather.precipitation}mm). Reduce production by 20-30% — fewer walk-ins expected.`,
        categories: [],
      })
    }
  }

  if (isHot) {
    const grillCats = filterCats(['steak', 'vors', 'khwanda'])
    if (grillCats.length > 0) {
      suggestions.push({
        type: 'increase',
        severity: 'MEDIUM',
        message: `High temps (${tomorrowWeather.tempMax}°C). Grilled items sell better. Consider increasing braai meats.`,
        categories: grillCats,
      })
    }
    const stewCats = filterCats(['mala', 'mashondzha'])
    if (stewCats.length > 0) {
      suggestions.push({
        type: 'reduce',
        severity: 'LOW',
        message: 'Heavy stews may sell less in heat. Consider slight reduction.',
        categories: stewCats,
      })
    }
    if (grillCats.length === 0 && stewCats.length === 0) {
      suggestions.push({
        type: 'maintain',
        severity: 'LOW',
        message: `High temps (${tomorrowWeather.tempMax}°C). Monitor which items sell best and adjust accordingly.`,
        categories: [],
      })
    }
  }

  if (isCold) {
    const warmCats = filterCats(['mala', 'chicken liver', 'mashondzha'])
    if (warmCats.length > 0) {
      suggestions.push({
        type: 'increase',
        severity: 'MEDIUM',
        message: `Cold day (${tomorrowWeather.tempMax}°C). Increase stews and warm comfort food.`,
        categories: warmCats,
      })
    }
    const papCats = filterCats(['pap only'])
    if (papCats.length > 0) {
      suggestions.push({
        type: 'increase',
        severity: 'LOW',
        message: 'Pap demand typically rises on cold days.',
        categories: papCats,
      })
    }
    if (warmCats.length === 0 && papCats.length === 0) {
      suggestions.push({
        type: 'maintain',
        severity: 'LOW',
        message: `Cold day (${tomorrowWeather.tempMax}°C). Warm items and comfort food may sell better — adjust production accordingly.`,
        categories: [],
      })
    }
  }

  if (isClear && !isHot && !isCold) {
    suggestions.push({
      type: 'maintain',
      severity: 'LOW',
      message: 'Clear, mild weather — standard production levels recommended.',
      categories: [],
    })
  }

  // Data-driven adjustments from correlations (already department-filtered via sales data)
  if (historicalCorrelations && historicalCorrelations.length > 0) {
    for (const corr of historicalCorrelations) {
      if (corr.confidence > 0.6) {
        // Only include if the category belongs to this department
        const cats = filterCats([corr.category])
        if (cats.length > 0) {
          suggestions.push({
            type: corr.direction,
            severity: 'MEDIUM',
            message: `Data pattern: ${corr.category} sales ${corr.direction === 'increase' ? 'rise' : 'drop'} ${corr.pctChange.toFixed(0)}% in similar weather (${corr.sampleSize} days analyzed).`,
            categories: cats,
            datadriven: true,
          })
        }
      }
    }
  }

  return suggestions
}

// Correlate historical sales with weather data
export function buildWeatherCorrelations(salesSummaries, weatherHistory) {
  if (!salesSummaries.length || !weatherHistory.length) return []

  const correlations = []
  const weatherMap = {}
  for (const w of weatherHistory) {
    weatherMap[w.date] = w
  }

  // Group sales by category
  const catSalesMap = {}
  for (const s of salesSummaries) {
    if (!s.category) continue
    if (!catSalesMap[s.category]) catSalesMap[s.category] = []
    const w = weatherMap[s.date]
    if (w) {
      catSalesMap[s.category].push({
        sales: s.actualSales || 0,
        temp: w.tempMax,
        precipitation: w.precipitation || 0,
        weatherCode: w.weatherCode,
        isRainy: (w.precipitation || 0) > 2,
        isHot: w.tempMax > 30,
        isCold: w.tempMax < 20,
      })
    }
  }

  for (const [category, entries] of Object.entries(catSalesMap)) {
    if (entries.length < 5) continue

    const allAvg = entries.reduce((s, e) => s + e.sales, 0) / entries.length

    // Rainy days vs dry days
    const rainy = entries.filter(e => e.isRainy)
    const dry = entries.filter(e => !e.isRainy)
    if (rainy.length >= 3 && dry.length >= 3) {
      const rainyAvg = rainy.reduce((s, e) => s + e.sales, 0) / rainy.length
      const dryAvg = dry.reduce((s, e) => s + e.sales, 0) / dry.length
      const pctChange = ((rainyAvg - dryAvg) / dryAvg) * 100
      if (Math.abs(pctChange) > 10) {
        correlations.push({
          category,
          condition: 'rainy',
          direction: pctChange > 0 ? 'increase' : 'reduce',
          pctChange: Math.abs(pctChange),
          sampleSize: rainy.length + dry.length,
          confidence: Math.min(1, (rainy.length + dry.length) / 20),
        })
      }
    }

    // Hot days vs normal
    const hot = entries.filter(e => e.isHot)
    const notHot = entries.filter(e => !e.isHot)
    if (hot.length >= 3 && notHot.length >= 3) {
      const hotAvg = hot.reduce((s, e) => s + e.sales, 0) / hot.length
      const notHotAvg = notHot.reduce((s, e) => s + e.sales, 0) / notHot.length
      const pctChange = ((hotAvg - notHotAvg) / notHotAvg) * 100
      if (Math.abs(pctChange) > 10) {
        correlations.push({
          category,
          condition: 'hot',
          direction: pctChange > 0 ? 'increase' : 'reduce',
          pctChange: Math.abs(pctChange),
          sampleSize: hot.length + notHot.length,
          confidence: Math.min(1, (hot.length + notHot.length) / 20),
        })
      }
    }

    // Cold days vs normal
    const cold = entries.filter(e => e.isCold)
    const notCold = entries.filter(e => !e.isCold)
    if (cold.length >= 3 && notCold.length >= 3) {
      const coldAvg = cold.reduce((s, e) => s + e.sales, 0) / cold.length
      const notColdAvg = notCold.reduce((s, e) => s + e.sales, 0) / notCold.length
      const pctChange = ((coldAvg - notColdAvg) / notColdAvg) * 100
      if (Math.abs(pctChange) > 10) {
        correlations.push({
          category,
          condition: 'cold',
          direction: pctChange > 0 ? 'increase' : 'reduce',
          pctChange: Math.abs(pctChange),
          sampleSize: cold.length + notCold.length,
          confidence: Math.min(1, (cold.length + notCold.length) / 20),
        })
      }
    }
  }

  return correlations.sort((a, b) => b.confidence - a.confidence)
}

// Auto-log today's weather when sales are saved
export async function autoLogTodayWeather() {
  const today = new Date().toISOString().split('T')[0]
  const existing = getWeatherForDate(today)
  if (existing) return existing

  const forecast = await fetchWeatherForecast()
  if (!forecast) return null

  const todayForecast = forecast.forecast.find(f => f.date === today)
  if (!todayForecast) return null

  return logWeatherForDate(today, {
    weatherCode: todayForecast.weatherCode,
    label: todayForecast.label,
    icon: todayForecast.icon,
    tempMax: todayForecast.tempMax,
    tempMin: todayForecast.tempMin,
    precipitation: todayForecast.precipitation,
    windMax: todayForecast.windMax,
  })
}
